import React, { useState, useEffect } from 'react';
import { useTabs } from '../hooks/useTabs';
import { useMobileNavigation } from '../hooks/useMobileNavigation';
import { useConfigContext } from '../contexts/ConfigContext';
import { getDirectoryTree } from '../services/contentService';
import Toolbar from '../components/Toolbar';
import Sidebar from '../components/Sidebar';
import DirectoryTree from '../components/DirectoryTree';
import SearchPanel from '../components/SearchPanel';
import TabBar from '../components/TabBar';
import ContentViewer from '../components/ContentViewer';
import './EditorPage.css';

function EditorPage() {
  const [activeView, setActiveView] = useState('tree');
  const [tree, setTree] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { config } = useConfigContext();
  const { openTabs, activeTabId, activeTab, openTab, closeTab, switchTab } = useTabs();
  const { addToHistory, goBack, goForward, canGoBack, canGoForward } = useMobileNavigation();

  // Load directory tree
  useEffect(() => {
    getDirectoryTree()
      .then(data => setTree(data))
      .catch(err => console.error('Failed to load tree:', err));
  }, []);

  const handleFileClick = (file) => {
    // Check if mobile view
    if (window.innerWidth <= 768) {
      // Mobile: Replace current content and update history
      addToHistory(file);
      openTab(file);
      setSidebarOpen(false); // Close sidebar after selection
    } else {
      // Desktop: Use tabs as normal
      openTab(file);
    }
  };

  const handleSearchResultClick = (result) => {
    if (window.innerWidth <= 768) {
      addToHistory(result);
      openTab(result);
      setSidebarOpen(false);
    } else {
      openTab(result);
    }
    setActiveView('tree'); // Switch back to tree view after opening from search
  };

  const handleAboutClick = () => {
    // Open About page as a special tab
    openTab({
      id: 'about',
      slug: 'about',
      title: 'About',
      path: 'about.md'
    });
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleMobileBack = () => {
    const previousFile = goBack();
    if (previousFile) {
      openTab(previousFile);
    }
  };

  const handleMobileForward = () => {
    const nextFile = goForward();
    if (nextFile) {
      openTab(nextFile);
    }
  };

  return (
    <div className={`editor-page ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <Toolbar
        activeView={activeView}
        onViewChange={setActiveView}
        onAboutClick={handleAboutClick}
        onToggleSidebar={toggleSidebar}
        sidebarOpen={sidebarOpen}
        onMobileBack={handleMobileBack}
        onMobileForward={handleMobileForward}
        canGoBack={canGoBack}
        canGoForward={canGoForward}
      />

      <Sidebar view={activeView} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}>
        {/* Mobile view switcher */}
        <div className="mobile-view-switcher">
          <button
            className={`view-switch-button ${activeView === 'tree' ? 'active' : ''}`}
            onClick={() => setActiveView('tree')}
          >
            <span className="view-switch-icon">≡</span>
            <span className="view-switch-label">Files</span>
          </button>
          <button
            className={`view-switch-button ${activeView === 'search' ? 'active' : ''}`}
            onClick={() => setActiveView('search')}
          >
            <span className="view-switch-icon">⌕</span>
            <span className="view-switch-label">Search</span>
          </button>
          <button
            className={`view-switch-button ${activeView === 'tags' ? 'active' : ''}`}
            onClick={() => setActiveView('tags')}
          >
            <span className="view-switch-icon">#</span>
            <span className="view-switch-label">Tags</span>
          </button>
        </div>

        {activeView === 'tree' && (
          <>
            <h3 className="sidebar-title">Files</h3>
            <div className="sidebar-tree-container">
              <DirectoryTree tree={tree} onFileClick={handleFileClick} />
            </div>
            {config.branding.enabled && (
              <div className="sidebar-branding">
                <span className="branding-text">{config.branding.text}</span>
              </div>
            )}
          </>
        )}

        {activeView === 'search' && (
          <>
            <h3 className="sidebar-title">Search</h3>
            <SearchPanel onResultClick={handleSearchResultClick} />
          </>
        )}

        {activeView === 'tags' && (
          <>
            <h3 className="sidebar-title">Tags</h3>
            <div className="tags-view">
              <p className="coming-soon">Tag browser coming soon!</p>
            </div>
          </>
        )}
      </Sidebar>

      <div className="content-area">
        <div className="mobile-header">
          <span className="mobile-branding">{config.branding.text}</span>
        </div>

        <TabBar
          tabs={openTabs}
          activeTabId={activeTabId}
          onTabClick={switchTab}
          onTabClose={closeTab}
        />

        <ContentViewer
          slug={activeTab?.slug}
        />
      </div>
    </div>
  );
}

export default EditorPage;
